/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { MATCH_STATUS, SPORT_ID } from 'adv-protocol/src/GameCommon'
import _ from 'lodash'
import { TimeLineWrap } from '@styles/match.style'
import { PeriodInfo, PeriodScore, SimpleMatchTimeline } from '@interface/chat'
import { observer } from 'mobx-react'
import { MatchRecordSimpleMatchInfo } from '@views/MatchRecord/index'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'

export interface Props {
  matchRecordSimpleMatchInfo: MatchRecordSimpleMatchInfo
  simpleMatchTimeline: SimpleMatchTimeline
}

interface ScoreInfo {
  homeScore: number
  awayScore: number
  homeWin: boolean
  awayWin: boolean
}

export const MatchTimeTable: React.FC<Props> = observer((props: Props) => {
  const {
    matchRecordSimpleMatchInfo,
    simpleMatchTimeline = { periodScoreList: [], periodInfoList: [] },
  } = props

  const { periodScoreList, periodInfoList } = simpleMatchTimeline
  const { sportId, homeTeamName, awayTeamName, isLiveMatch } = matchRecordSimpleMatchInfo

  let periodCount = _.isEmpty(periodScoreList) ? _.size(periodInfoList) : _.size(periodScoreList)
  if (isLiveMatch && _.size(periodScoreList) < _.size(periodInfoList)) {
    periodCount = _.size(periodInfoList)
  }

  const tableRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState<'left' | 'right' | ''>('')

  const handleTableScroll = () => {
    const el = tableRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el

    if (scrollWidth <= clientWidth) {
      setScrollState('')
      return
    }

    if (scrollLeft <= 0) {
      setScrollState('left')
    } else if (scrollLeft + clientWidth >= scrollWidth - 1) {
      setScrollState('right')
    }
  }

  useLayoutEffect(() => {
    const el = tableRef.current
    if (!el) return

    el.addEventListener('scroll', handleTableScroll)
    const raf = requestAnimationFrame(handleTableScroll)

    // 테이블 가로 스크롤인 경우 PC에서 드래그 기능
    let isDown = false
    let startX = 0
    let scrollLeft = 0

    const onMouseDown = (e: MouseEvent) => {
      isDown = true
      startX = e.pageX - el.offsetLeft
      scrollLeft = el.scrollLeft
      el.style.cursor = 'grabbing'
    }
    const onMouseLeave = () => {
      isDown = false
      el.style.cursor = 'grab'
    }
    const onMouseUp = () => {
      isDown = false
      el.style.cursor = 'grab'
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()

      const x = e.pageX - el.offsetLeft
      const walk = x - startX
      el.scrollLeft = scrollLeft - walk
    }

    el.addEventListener('mousedown', onMouseDown)
    el.addEventListener('mouseleave', onMouseLeave)
    el.addEventListener('mouseup', onMouseUp)
    el.addEventListener('mousemove', onMouseMove)

    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('scroll', handleTableScroll)
      el.removeEventListener('mousedown', onMouseDown)
      el.removeEventListener('mouseleave', onMouseLeave)
      el.removeEventListener('mouseup', onMouseUp)
      el.removeEventListener('mousemove', onMouseMove)
    }
  }, [periodScoreList, periodInfoList])

  const addBlankScore
    = (beforePeriodScore: PeriodScore | PeriodInfo, currentPeriodScore: PeriodScore | PeriodInfo): boolean => {
    return (sportId === SPORT_ID.SOCCER || sportId === SPORT_ID.HANDBALL)
      && currentPeriodScore.matchStatusCode === MATCH_STATUS.Penalties
      && !_.isNil(beforePeriodScore)
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.Overtime    // 연장전이 없는 경기가 있음
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.FirstExtra  // 연장 전, 후반으로 나뉜 경기 중 전반만 존재하는 경기가 있음
      && beforePeriodScore.matchStatusCode !== MATCH_STATUS.SecondExtra  // 연장 전, 후반으로 나뉜 경기 중 후반까지 존재하는 경기가 있음
  }

  /**
   * betradar match summary 로 스코어정보 생성
   */
  const makeScoreInfoByPeriodScore = (): ScoreInfo[] => {
    const scoreList: ScoreInfo[] = []
    let beforePeriodScore: PeriodScore | PeriodInfo
    for (const periodScore of periodScoreList) {
      if (addBlankScore(beforePeriodScore, periodScore)) {
        scoreList.push({
          homeScore: null,
          awayScore: null,
          homeWin: null,
          awayWin: null,
        })
      }

      scoreList.push({
        homeScore: periodScore.homeScore,
        awayScore: periodScore.awayScore,
        homeWin: periodScore.homeScore > periodScore.awayScore,
        awayWin: periodScore.homeScore < periodScore.awayScore,
      })

      beforePeriodScore = periodScore
    }
    return scoreList
  }

  /**
   * betradar timeline 으로 스코어정보 생성.
   * 기본적으로는 match summary 로 정보를 생성하지만 match summary 가 없을 경우에 대비
   * 또한 라이브 경기는 match summary 가 쿼터나 이닝이 끝나야 생성되므로 timeline 정보로 스코어를 생성한다.
   */
  const makeScoreInfoByPeriodInfo = (): ScoreInfo[] => {
    const scoreList: ScoreInfo[] = []
    let beforePeriodScore: PeriodScore | PeriodInfo
    for (const periodInfo of periodInfoList) {
      if ((sportId === SPORT_ID.SOCCER || sportId === SPORT_ID.HANDBALL)
        && periodInfo.matchStatusCode === MATCH_STATUS.SecondExtra) { // 축구 연장 후반
        scoreList[2].homeScore += periodInfo.homeScore
        scoreList[2].awayScore += periodInfo.awayScore
        scoreList[2].homeWin = scoreList[2].homeScore > scoreList[2].awayScore
        scoreList[2].awayWin = scoreList[2].homeScore < scoreList[2].awayScore
      } else {
        if (addBlankScore(beforePeriodScore, periodInfo)) {
          scoreList.push({
            homeScore: null,
            awayScore: null,
            homeWin: null,
            awayWin: null,
          })
        }
        scoreList.push({
          homeScore: periodInfo.homeScore,
          awayScore: periodInfo.awayScore,
          homeWin: periodInfo.homeScore > periodInfo.awayScore,
          awayWin: periodInfo.homeScore < periodInfo.awayScore,
        })
      }
      beforePeriodScore = periodInfo
    }
    return scoreList
  }

  const makeScoreInfoList = (): ScoreInfo[] => {
    let scoreList: ScoreInfo[] = []
    if (isLiveMatch && _.size(periodScoreList) < _.size(periodInfoList)) {
      scoreList = makeScoreInfoByPeriodInfo()
    } else if (!_.isEmpty(periodScoreList)) {
      scoreList = makeScoreInfoByPeriodScore()
    } else if (!_.isEmpty(periodInfoList)) {
      scoreList = makeScoreInfoByPeriodInfo()
    }
    return scoreList
  }

  const makeMatchTimeTable = (basePeriod: number) => {
    const scoreList = makeScoreInfoList()
    const homeTotalScore = _.sumBy(scoreList, (s) => s.homeScore || 0)
    const awayTotalScore = _.sumBy(scoreList, (s) => s.awayScore || 0)
    const totalSum = homeTotalScore + awayTotalScore

    const elems = [
      <tr key="timetable-event-table-row-home">
        <td key="timetable-event-home-item-name" className="team-name fixed left">{homeTeamName}</td>
        {_.range(basePeriod).map(v => {
          const homeScore = scoreList[v]?.homeScore ?? '-'
          const scoreClassName = classNames('score', {
            win: scoreList[v]?.homeWin,
          })
          const key = `timeline-event-away-item-${v}`
          return <td key={key} className={scoreClassName}>{homeScore}</td>
        })}
        {(sportId === SPORT_ID.BASEBALL || sportId === SPORT_ID.VOLLEYBALL || sportId === SPORT_ID.BASKETBALL)
          && _.size(scoreList) > basePeriod && (
            _.range(basePeriod, _.size(scoreList)).map((v) => {
            const homeScore = scoreList[v]?.homeScore ?? '-'
            const scoreClassName = classNames('score', {
              win: scoreList[v]?.homeWin,
            })
            const key = `timeline-event-away-item-${v}`
            return <td key={key} className={scoreClassName}>{homeScore}</td>
          })
        )}
        {/*합계 : 농구, 배구, 탁구, 테니스*/}
        {sportId === SPORT_ID.BASKETBALL
          // 농구 총합계
          ? <td rowSpan={2} className="score total fixed right">{totalSum}</td>
          : [SPORT_ID.VOLLEYBALL, SPORT_ID.TABLE_TENNIS, SPORT_ID.TENNIS].includes(sportId)
            ? (
              <td rowSpan={2} className="fixed right combined-total-column">
                <div className="combined-total-wrap">
                  <div className="team-totals">
                    <div className={classNames('score', { win: homeTotalScore > awayTotalScore })}>
                      {homeTotalScore}
                    </div>
                    <div className={classNames('score', { win: awayTotalScore > homeTotalScore })}>
                      {awayTotalScore}
                    </div>
                  </div>
                  <div className="score total overall-total">
                    {totalSum}
                  </div>
                </div>
              </td>
            )
            : null
        }
      </tr>,
      <tr key="timetable-event-table-row-away">
        <td key="timetable-event-away-item-name" className="team-name fixed left">{awayTeamName}</td>
        {_.range(basePeriod).map(v => {
          const awayScore = scoreList[v]?.awayScore ?? '-'
          const scoreClassName = classNames('score', {
            win: scoreList[v]?.awayWin,
          })
          const key = `timeline-event-away-item-${v}`
          return <td key={key} className={scoreClassName}>{awayScore}</td>
        })}
        {(sportId === SPORT_ID.BASEBALL || sportId === SPORT_ID.VOLLEYBALL || sportId === SPORT_ID.BASKETBALL)
          && _.size(scoreList) > basePeriod && (
          _.range(basePeriod, _.size(scoreList)).map((v) => {
            const awayScore = scoreList[v]?.awayScore ?? '-'
            const scoreClassName = classNames('score', {
              win: scoreList[v]?.awayWin,
            })
            const key = `timeline-event-away-item-${v}`
            return <td key={key} className={scoreClassName}>{awayScore}</td>
          })
        )}
        {/*합계 : 농구, 배구, 탁구, 테니스 - 홈팀 행의 rowSpan으로 인해 여기서는 null 처리*/}
        {sportId === SPORT_ID.BASKETBALL
          ? null
          : [SPORT_ID.VOLLEYBALL, SPORT_ID.TABLE_TENNIS, SPORT_ID.TENNIS].includes(sportId)
            ? null
            : null
        }
      </tr>,
    ]
    if (sportId === SPORT_ID.BASEBALL) {
      elems.reverse()
    }
    return (
      <tbody className="table--body">
      {elems}
      </tbody>
    )
  }
  const makeTimeTable = () => {
    const leftHeadClass = classNames('fixed left', {
      'more': scrollState !== 'left' && scrollState !== ''
    })
    const rightHeadClass = classNames('round fixed right', {
      'more': scrollState !== 'right' && scrollState !== ''
    })
    const baseballHeadClass = classNames('fixed left', {
      'more-left': scrollState !== 'left' && scrollState !== '',
      'more-right': scrollState !== 'right' && scrollState !== '',
      'pc': !isMobile,
    })

    let contents
    switch (sportId) {
      case SPORT_ID.SOCCER:
      case SPORT_ID.HANDBALL:
        contents = (
          <div className="timeline--table soccer">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="team-name">팀</th>
                <th className="round">전반</th>
                <th className="round">후반</th>
                <th className="round">연장</th>
                <th className="round">승부</th>
              </tr>
              </thead>
              {makeMatchTimeTable(4)}
            </table>
          </div>
        )
        break
      case SPORT_ID.WATERPOLO: // 수구
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="fixed left">팀</th>
                <th className="round">1쿼터</th>
                <th className="round">2쿼터</th>
                <th className="round">3쿼터</th>
                <th className="round">4쿼터</th>
                {periodCount > 4 && (
                  <th className="round">연장</th>
                )}
              </tr>
              </thead>
              {makeMatchTimeTable(periodCount)}
            </table>
          </div>
        )
        break
      case SPORT_ID.BASKETBALL:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className={leftHeadClass}>팀</th>
                <th className="round">1Q</th>
                <th className="round">2Q</th>
                <th className="round">3Q</th>
                <th className="round">4Q</th>
                {periodCount > 4 && (
                  <th className="round">연장</th>
                )}
                <th className={rightHeadClass}>합계</th>
              </tr>
              </thead>
              {makeMatchTimeTable(Math.max(4, periodCount))}
            </table>
          </div>
        )
        break
      case SPORT_ID.BASEBALL:
        contents = (
          <div  ref={tableRef} className="timeline--table baseball">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className={baseballHeadClass}>팀</th>
                <th className="round">1</th>
                <th className="round">2</th>
                <th className="round">3</th>
                <th className="round">4</th>
                <th className="round">5</th>
                <th className="round">6</th>
                <th className="round">7</th>
                <th className="round">8</th>
                <th className="round">9</th>
                {periodCount > 9 && (
                  _.range(9, periodCount).map((v) => {
                    const key = `timeline-baseball-head-${v}`
                    return <th key={key} className="round overtime">연장</th>
                  })
                )}
              </tr>
              </thead>
              {makeMatchTimeTable(9)}
            </table>
          </div>
        )
        break
      case SPORT_ID.VOLLEYBALL:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className={leftHeadClass}>팀</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
                {periodCount > 5 && (
                  _.range(5, periodCount).map((v) => {
                    const key = `timeline-volleyball-head-${v}`
                    return <th key={key} className="round">{v}세트</th>
                  })
                )}
                <th colSpan={2} className={rightHeadClass}>합계</th>
              </tr>
              </thead>
              {makeMatchTimeTable(5)}
            </table>
          </div>
        )
        break
      case SPORT_ID.ICE_HOCKEY:
        contents = (
          <div className="timeline--table icehockey">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className="team-name">팀</th>
                <th className="round">1P</th>
                <th className="round">2P</th>
                <th className="round">3P</th>
                {periodCount > 3 && (<th className="round">연장</th>)}
                {periodCount === 5 && (<th className="round">승부</th>)}
              </tr>
              </thead>
              {makeMatchTimeTable(Math.max(3, periodCount))}
            </table>
          </div>
        )
        break
      case SPORT_ID.TENNIS:
        contents = (
          <div className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className={leftHeadClass}>선수</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
                <th className={rightHeadClass} colSpan={2}>합계</th>
              </tr>
              </thead>
              {makeMatchTimeTable(5)}
            </table>
          </div>
        )
        break
      case SPORT_ID.TABLE_TENNIS:
        contents = (
          <div ref={tableRef} className="timeline--table">
            <table className="table">
              <thead className="table--head">
              <tr>
                <th className={leftHeadClass}>선수</th>
                <th className="round">1세트</th>
                <th className="round">2세트</th>
                <th className="round">3세트</th>
                <th className="round">4세트</th>
                <th className="round">5세트</th>
                <th className="round">6세트</th>
                <th className="round">7세트</th>
                <th className={rightHeadClass} colSpan={2}>합계</th>
              </tr>
              </thead>
              {makeMatchTimeTable(7)}
            </table>
          </div>
        )
        break
      default:
        break
    }
    return contents
  }

  const wrapClassName = classNames('timeline', {
    'timeline-none': _.isEmpty(makeTimeTable()),
  })

  return (
    <TimeLineWrap className={wrapClassName}>
      {makeTimeTable()}
    </TimeLineWrap>
  )
})
